'use client';
import React, { useState, createContext, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { LucideIcon } from 'lucide-react';

const EasyTabContext = createContext<
  | {
      selectedTab: string;
      setSelectedTab: (tab: string) => void;
    }
  | undefined
>(undefined);

interface EasyTabProps<T extends string = string> {
  children: React.ReactNode;
  defaultTab?: T;
  className?: string;
  onTabChanged?: (tab: T) => void;
}

let easyTabCounter = 0;

export const EasyTab = <T extends string = string>({
  children,
  defaultTab,
  className,
  onTabChanged,
}: EasyTabProps<T>) => {
  const [selectedTab, setSelectedTab] = useState<T | ''>(defaultTab || '');
  const uniqueId = React.useMemo(() => `easy-tab-${easyTabCounter++}`, []);

  const handleTabChange = (tab: string) => {
    const typedTab = tab as T;
    setSelectedTab(typedTab);
    if (onTabChanged) {
      onTabChanged(typedTab);
    }
  };

  return (
    <EasyTabContext.Provider
      value={{ selectedTab: selectedTab as string, setSelectedTab: handleTabChange }}>
      <div className={className}>{children}</div>
    </EasyTabContext.Provider>
  );
};

interface EasyTabItemProps<T extends string> {
  label: T;
  className?: string;
  whenActiveClassName?: string;
  whenActiveTextClassName?: string;
  useBottomActiveBorder?: boolean;
  useFullBorderWhenActive?: boolean;
  icon?: LucideIcon | JSX.Element;
  hideLabel?: boolean;
  disabled?: boolean;
  showLbel?: boolean;
  textClassName?: string;
}

export const EasyTabItem = <T extends string>({
  label,
  className,
  useBottomActiveBorder = true,
  useFullBorderWhenActive = false,
  whenActiveClassName,
  whenActiveTextClassName,
  textClassName,
  icon,
  disabled = false,
  showLbel = true,
}: EasyTabItemProps<T>) => {
  const context = useContext(EasyTabContext);

  if (!context) {
    throw new Error('EasyTabItem must be used within an EasyTab');
  }

  const { selectedTab, setSelectedTab } = context;
  const isActive = selectedTab === label;
  const uniqueId = React.useMemo(() => `easy-tab-${easyTabCounter++}`, []);

  const renderIcon = () => {
    if (!icon) return null;
    if (typeof icon === 'function') {
      // Assume it's a LucideIcon
      return icon({ size: 16, stroke: selectedTab === label ? '#FF600D' : '#707B8F' });
    }
    // Assume it's a JSX.Element
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement, {
        style: { stroke: selectedTab === label ? '#FF600D' : '#707B8F' },
      });
    }
    return null;
  };

  return (
    <div
      className={twMerge(
        'cursor-pointer',
        disabled && 'cursor-not-allowed opacity-50',
        useFullBorderWhenActive ? (isActive ? 'border-primary border bg-tabActive' : '') : '',
        whenActiveClassName && isActive ? whenActiveClassName : '',
        className,
      )}
      onClick={() => {
        if (!disabled) {
          setSelectedTab(label);
        }
      }}>
      <div
        className={twMerge(
          'text-sm capitalize font-medium flex items-center justify-center gap-2',
          isActive && !disabled
            ? whenActiveTextClassName
              ? whenActiveTextClassName
              : 'text-primary'
            : '',
          textClassName,
          disabled ? 'text-gray-400' : '',
        )}>
        {renderIcon()}
        {showLbel && label}
      </div>
      {isActive && useBottomActiveBorder && !disabled && (
        <motion.div
          className="underline border border-primary"
          layoutId={`underline-${uniqueId}-${label}`}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        />
      )}
    </div>
  );
};

interface EasyTabContentProps<T extends string> {
  label: T;
  children: React.ReactNode;
  className?: string;
}

export const EasyTabContent = <T extends string>({
  label,
  children,
  className,
}: EasyTabContentProps<T>) => {
  const context = useContext(EasyTabContext);

  if (!context) {
    throw new Error('EasyTabContent must be used within an EasyTab');
  }

  const { selectedTab } = context;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={selectedTab}
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -8, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className={twMerge(className, selectedTab === label ? '' : 'hidden')}>
        {children}
      </motion.div>
    </AnimatePresence>
  );
};
